/**
 * @fileoverview Discover usage telemetry (WEB-1229, ruling R14).
 *
 * Exactly three events. Do not add a fourth: later Discover waves decide on
 * real usage, and that number stays readable only while the set is fixed.
 *
 * | PostHog event             | Trigger                                              | Properties                                                                       |
 * | ------------------------- | ---------------------------------------------------- | -------------------------------------------------------------------------------- |
 * | `discover_viewed`         | Once per surface per mount, when the total is known  | `surface`, `city`, `host`, `date_keyword`, `active_filter_count`, `result_total` |
 * | `discover_filter_changed` | A filter in the URL changed and its total landed     | `filter_key`, `value_kind`, `result_total`                                       |
 * | `discover_event_opened`   | An event card link is clicked                        | `event_id`, `position`, `sort`, `surface`                                        |
 *
 * Every event also carries `is_internal` (see `./internal.ts`).
 *
 * **No free text.** The search query is a filter key (`q`) like any other: the
 * events say *that* it changed, never what it says. No value of any filter is
 * sent, only its key and the kind of change.
 *
 * Capture goes through `lib/analytics.ts#track`, so nothing is sent before the
 * visitor grants consent.
 */
import { track } from '@/lib/analytics';
import { countActiveFilters } from '@/lib/discover/activeFilterCount';
import type { DiscoverParams, SortMode, ViewMode } from '@/lib/discover/url-state';

export const DISCOVER_EVENT_NAMES = {
  DISCOVER_VIEWED: 'discover_viewed',
  DISCOVER_FILTER_CHANGED: 'discover_filter_changed',
  DISCOVER_EVENT_OPENED: 'discover_event_opened',
} as const;

export type DiscoverEventName = (typeof DISCOVER_EVENT_NAMES)[keyof typeof DISCOVER_EVENT_NAMES];

/** The two Discover views. Same vocabulary as the `view` URL param. */
export type DiscoverSurface = ViewMode;

/** Every `DiscoverParams` key that narrows results. `view` and `sort` do not. */
export type DiscoverFilterKey = Exclude<keyof DiscoverParams, 'view' | 'sort'>;

/**
 * How a filter changed. Describes the change, never the value:
 * `set` (was empty), `added` / `removed` (a multi-select grew or shrank),
 * `replaced` (a different value, same arity), `cleared` (now empty).
 */
export type DiscoverFilterValueKind = 'set' | 'added' | 'removed' | 'replaced' | 'cleared';

export interface DiscoverFilterChange {
  /** The changed key, or `multiple` when one URL write moved several. */
  filterKey: DiscoverFilterKey | 'multiple';
  valueKind: DiscoverFilterValueKind;
}

/** Date presets sent as-is. Anything else is a custom range. */
const DATE_KEYWORDS: ReadonlySet<string> = new Set([
  'tonight',
  'tomorrow',
  'weekend',
  'next7',
  'next30',
  'this-week',
]);

/** `any` when unset, the preset keyword, or `custom` for an explicit range. */
export function discoverDateKeyword(date: DiscoverParams['date']): string {
  if (!date) return 'any';
  return DATE_KEYWORDS.has(date) ? date : 'custom';
}

function isEmptyFilterValue(value: unknown): boolean {
  if (value === undefined || value === null || value === false || value === '') return true;
  return Array.isArray(value) && value.length === 0;
}

function sameFilterValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function classifyChange(previous: unknown, next: unknown): DiscoverFilterValueKind {
  if (isEmptyFilterValue(next)) return 'cleared';
  if (isEmptyFilterValue(previous)) return 'set';
  if (Array.isArray(previous) && Array.isArray(next)) {
    if (next.length > previous.length) return 'added';
    if (next.length < previous.length) return 'removed';
  }
  return 'replaced';
}

/**
 * The filter change between two URL states, or `null` when no filter moved
 * (a `view` or `sort` change, or the same URL re-parsed). Keys are read off
 * the params objects so a facet added to or removed from `DiscoverParams` is
 * picked up without touching this function.
 */
export function diffDiscoverFilters(
  previous: DiscoverParams,
  next: DiscoverParams,
): DiscoverFilterChange | null {
  const keys = new Set<string>([...Object.keys(previous), ...Object.keys(next)]);
  keys.delete('view');
  keys.delete('sort');
  const changed: Array<{ key: DiscoverFilterKey; kind: DiscoverFilterValueKind }> = [];
  for (const key of keys) {
    const filterKey = key as DiscoverFilterKey;
    const before: unknown = previous[filterKey];
    const after: unknown = next[filterKey];
    if (isEmptyFilterValue(before) && isEmptyFilterValue(after)) continue;
    if (sameFilterValue(before, after)) continue;
    changed.push({ key: filterKey, kind: classifyChange(before, after) });
  }
  if (changed.length === 0) return null;
  if (changed.length === 1) {
    const [only] = changed;
    return { filterKey: only.key, valueKind: only.kind };
  }
  return {
    filterKey: 'multiple',
    valueKind: changed.every((change) => change.kind === 'cleared') ? 'cleared' : 'replaced',
  };
}

export interface DiscoverViewedPayload {
  surface: DiscoverSurface;
  city: string;
  host: string;
  params: DiscoverParams;
  resultTotal: number | null;
  isInternal: boolean;
}

export function trackDiscoverViewed(payload: DiscoverViewedPayload): void {
  track(DISCOVER_EVENT_NAMES.DISCOVER_VIEWED, {
    surface: payload.surface,
    city: payload.city,
    host: payload.host,
    date_keyword: discoverDateKeyword(payload.params.date),
    active_filter_count: countActiveFilters(payload.params),
    result_total: payload.resultTotal,
    is_internal: payload.isInternal,
  });
}

export interface DiscoverFilterChangedPayload extends DiscoverFilterChange {
  /** Total for the new filter set; `null` when a newer change superseded it first. */
  resultTotal: number | null;
  isInternal: boolean;
}

export function trackDiscoverFilterChanged(payload: DiscoverFilterChangedPayload): void {
  track(DISCOVER_EVENT_NAMES.DISCOVER_FILTER_CHANGED, {
    filter_key: payload.filterKey,
    value_kind: payload.valueKind,
    result_total: payload.resultTotal,
    is_internal: payload.isInternal,
  });
}

export interface DiscoverEventOpenedPayload {
  eventId: string;
  /** 0-indexed position of the card among the rendered Discover cards. */
  position: number;
  sort: SortMode;
  surface: DiscoverSurface;
  isInternal: boolean;
}

export function trackDiscoverEventOpened(payload: DiscoverEventOpenedPayload): void {
  track(DISCOVER_EVENT_NAMES.DISCOVER_EVENT_OPENED, {
    event_id: payload.eventId,
    position: payload.position,
    sort: payload.sort,
    surface: payload.surface,
    is_internal: payload.isInternal,
  });
}
